import { useEffect, useState } from 'react';
import { handleRouteError } from '../../../../logger';
import { BaseChartComponentProps, BaseHistoricalUtr, ChartDataProps } from '../types';

/**
 * Custom hook for fetching chart data.
 * Handles the common pattern of loading data when calculation changes.
 *
 * @param getChartData - Async function to fetch chart data
 * @param props - Chart component props containing utrsData, variables, calculation, currencyConfig
 * @returns The fetched data or undefined if loading/invalid
 */
export const useChartData = <T extends BaseHistoricalUtr, D>(
  getChartData: (props: ChartDataProps<T>) => Promise<D>,
  props: Pick<BaseChartComponentProps<T>, 'utrsData' | 'variables' | 'calculation' | 'currencyConfig'>
): D | undefined => {
  const [data, setData] = useState<D | undefined>();
  const { utrsData, variables, calculation, currencyConfig } = props;
  // Extract primitive values to avoid reference changes triggering re-fetches
  const currencySymbol = currencyConfig?.currencySymbol;
  const currencyCode = currencyConfig?.currencyCode;
  const surveyUnitConfigMap = currencyConfig?.surveyUnitConfigMap;

  useEffect(() => {
    if (calculation === undefined || variables === undefined) {
      return;
    }
    // Ignore late settlements from a previous render's promise — without this guard a stale
    // rejection would call setData(undefined) and wipe the chart frame from a newer, successful
    // fetch.
    let ignore = false;
    // Reconstruct currencyConfig from tracked primitives to satisfy lint rules
    const stableCurrencyConfig =
      surveyUnitConfigMap ? { currencySymbol, currencyCode, surveyUnitConfigMap } : undefined;
    getChartData({ utrsData, variables, calculation, currencyConfig: stableCurrencyConfig })
      .then((result) => {
        if (ignore) {
          return;
        }
        setData(result);
      })
      .catch((err: unknown) => {
        if (ignore) {
          return;
        }
        // Blank on real rejection so the user can see the new props' fetch failed —
        // unlike the cleanup path (data stays while a new fetch is in flight), there's
        // no in-flight request to wait for, and stale data would misrepresent the
        // current configuration. Sentry still gets the error.
        handleRouteError(err, { context: 'useChartData' });
        setData(undefined);
      });
    // Stale-while-revalidating: keep the previous data visible while the new fetch runs,
    // so a parent re-render that changes any dep doesn't blank the chart between renders.
    // The ignore flag still blocks late settlements from a superseded fetch.
    return () => {
      ignore = true;
    };
  }, [calculation, getChartData, utrsData, variables, currencySymbol, currencyCode, surveyUnitConfigMap]);

  return data;
};
